import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Paperclip, Star, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { useScrollLock } from '@/lib/useScrollLock'
import { labelClass, labelStyle } from '@/lib/styleKit'
import { cn } from '@/lib/utils'
import type { FeedbackMineItem } from '@/types/feedback'
import { TONE_CLASS, formatReportDate, kindLabel, statusTone } from './myReportsLogic'

interface ReportDetailModalProps {
  report: FeedbackMineItem | null
  onClose: () => void
}

const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className={labelClass} style={labelStyle}>
        {label}
      </span>
      <div className="whitespace-pre-wrap break-words text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  )
}

/**
 * Everything the reporter submitted for one report, plus where it stands.
 * Same chrome and a11y contract as ConfirmDialog (portal, blur backdrop,
 * Escape, focus trap, focus restore, scroll lock), minus the confirm/cancel
 * semantics: it is a read-only sheet with one Close.
 */
export default function ReportDetailModal({ report, onClose }: ReportDetailModalProps) {
  const open = report !== null
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const restoreRef = useRef<HTMLElement | null>(null)
  const uid = useId()
  const titleId = `${uid}-title`

  useScrollLock(open)

  useEffect(() => {
    if (!open) return
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null
    closeRef.current?.focus()
    return () => {
      const previous = restoreRef.current
      restoreRef.current = null
      if (previous?.isConnected && typeof previous.focus === 'function') previous.focus()
    }
  }, [open])

  useEffect(() => {
    const node = dialogRef.current
    if (!open || !node) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
        e.preventDefault()
        e.stopPropagation()
        if (focusables.length === 0) return
        const current = focusables.indexOf(document.activeElement as HTMLElement)
        const next =
          current === -1
            ? e.shiftKey ? focusables[focusables.length - 1] : focusables[0]
            : e.shiftKey
              ? focusables[(current === 0 ? focusables.length : current) - 1]
              : focusables[(current + 1) % focusables.length]
        next?.focus()
      }
    }
    node.addEventListener('keydown', onKey)
    return () => node.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {report && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed top-0 left-0 w-screen h-screen"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 4 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border bg-card p-6 shadow-2xl outline-none',
              'border-border/60 dark:border-white/[0.08] dark:bg-[#191a1b]',
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground dark:text-[#8a8f98]">
                  <span>{kindLabel(report)}</span>
                  {report.reference_code && <span className="font-mono">{report.reference_code}</span>}
                  <span className={cn('rounded-md px-2 py-0.5 font-medium', TONE_CLASS[statusTone(report.user_status)])}>
                    {report.user_status}
                  </span>
                </div>
                <h2 id={titleId} className="mt-1.5 text-lg font-emphasis tracking-tight text-foreground dark:text-[#f7f8f8]">
                  {report.type === 'rating' && report.rating != null ? (
                    <span className="inline-flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={16} weight={n <= (report.rating ?? 0) ? 'fill' : 'regular'} className={n <= (report.rating ?? 0) ? 'text-amber-500' : 'text-muted-foreground/30'} />
                      ))}
                      <span className="ml-1">{report.rating}/5</span>
                    </span>
                  ) : (
                    report.title
                  )}
                </h2>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                aria-label="Dismiss"
                className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:hover:bg-white/[0.06]"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <Row label={report.type === 'rating' ? 'Reason' : 'Description'}>{report.description || '—'}</Row>
              {report.steps_to_reproduce && <Row label="Steps to reproduce">{report.steps_to_reproduce}</Row>}
              {report.problem_solved && <Row label="Problem it solves">{report.problem_solved}</Row>}

              <div className="grid grid-cols-2 gap-4">
                {report.module && <Row label={report.type === 'bug' ? 'Module' : 'Area'}>{report.module}</Row>}
                {report.severity && <Row label="Severity">{report.severity}</Row>}
                {report.importance && <Row label="Importance">{report.importance}</Row>}
                <Row label="Submitted">{formatReportDate(report.created_at) || '—'}</Row>
                {report.resolved_at && <Row label="Resolved">{formatReportDate(report.resolved_at)}</Row>}
                {report.page_url && <Row label="Page">{report.page_url}</Row>}
                {report.app_version && <Row label="App version">{report.app_version}</Row>}
              </div>

              {report.has_attachment && (
                <p className="inline-flex items-center gap-1 text-xs text-muted-foreground dark:text-[#8a8f98]">
                  <Paperclip size={13} /> An attachment was included with this report.
                </p>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
